import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import 'pure-react-carousel/dist/react-carousel.es.css';
import React, { useState, useContext } from 'react';
import { GlobalContext } from '../context/GlobalState';
import { languages, currency } from 'constants';
import { Helmet } from 'react-helmet';
import { isSateen300 } from '../utils/productCodes';
import PriceRangeLine from './PriceRangeLine';

// The product payload only carries `materialAttributeId`, never the material's name, so resolve it
// here. Codes carrying the 300-thread-count suffix are the premium sateen - the grid card reads the
// same shared rule, so the two labels can no longer drift apart.
const MATERIAL_NAMES = { 1: 'Памучен сатен', 2: 'Ранфорс' };

// Once a variation is chosen we show one exact EUR price. This is the loudest number on the page,
// so it is bold and large.
export const variationPriceSx = {
  fontSize: '1.6rem',
  fontWeight: 700,
  color: 'gold.text',
  lineHeight: 1.2
};
export const variationCurrencySx = { fontSize: '0.8rem', fontWeight: 500, letterSpacing: '0.04em' };

// The range keeps the same shape as the exact price - large amount, small currency code - but a
// step quieter: ink rather than gold, lighter, smaller. Choosing a size then visibly promotes it.
const rangePriceSx = { fontSize: '1.25rem', fontWeight: 500, color: 'price.main', lineHeight: 1.2 };

const resolveMaterialName = (product) => {
  if (isSateen300(product?.code)) return 'Памучен сатен 300 ТС';
  return MATERIAL_NAMES[product?.materialAttributeId] || null;
};

export default function ProductViewGridItemIntro({
  product,
  priceRange,
  additionalProductData,
  variationPrice
}) {
  const { lang } = useContext(GlobalContext);
  const [summary, setSummary] = useState([]);
  const [images, setImages] = useState([]);

  React.useEffect(() => {
    if (product && product.images) {
      const temp = product['images'].sort(function (a, b) {
        return a['Product_Image'].position - b['Product_Image'].position;
      });
      setImages(temp);
    }
    if (
      additionalProductData &&
      languages.bg === lang &&
      'summary_bg' in additionalProductData &&
      typeof additionalProductData.summary_bg !== 'undefined' &&
      additionalProductData.summary_bg != null
    ) {
      let summary_result = additionalProductData.summary_bg.split(';').map(function (item) {
        return item.trim();
      });
      summary_result = summary_result.filter((element) => {
        return element !== '';
      });
      setSummary(summary_result);
    }
  }, [additionalProductData]);

  const buildMetaDescription = () => {
    const parts = [];
    if (product.name_bg) parts.push(product.name_bg);
    if (summary && summary.length > 0) parts.push(summary[0]);
    if (priceRange && priceRange.length > 0) {
      parts.push(
        `Цена: ${priceRange.map((price) => parseFloat(price).toFixed(2)).join(' - ')} EUR`
      );
    }
    parts.push('100% памук, OEKO-TEX сертификат. Поръчай от GoToBed.eu');
    const desc = parts.join(' | ');
    return desc.length > 160 ? desc.substring(0, 157) + '...' : desc;
  };

  const metaDescription = buildMetaDescription();
  const canonicalUrl = window.location.origin + window.location.pathname;

  return (
    <>
      <Helmet>
        <title>{product.name_bg} | GoToBed</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={product.name_bg} />
        <meta property="og:description" content={metaDescription} />
        {images && images.length > 0 && <meta property="og:image" content={images[0].url} />}
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="GoToBed" />
        {priceRange && priceRange.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: product.name_bg,
              image: images && images.length > 0 ? images[0].url : undefined,
              description: summary && summary.length > 0 ? summary[0] : product.name_bg,
              sku: product.code,
              brand: { '@type': 'Brand', name: 'GoToBed' },
              offers: {
                '@type': 'AggregateOffer',
                lowPrice: parseFloat(priceRange[0]).toFixed(2),
                highPrice: parseFloat(priceRange[1] || priceRange[0]).toFixed(2),
                priceCurrency: currency.EUR,
                availability: 'https://schema.org/InStock',
                url: canonicalUrl
              }
            })}
          </script>
        )}
      </Helmet>
      <Grid item xs={12} sm={5} style={{ backgroundColor: '' }}>
        {resolveMaterialName(product) && (
          <Typography
            variant="overline"
            sx={{ display: 'block', px: 1, pt: 1, color: 'gold.text' }}>
            {resolveMaterialName(product)}
          </Typography>
        )}
        <Typography
          style={{
            wordWrap: 'break-word',
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3
          }}
          // No bottom padding: `overflow: hidden` clips at the padding box, so any padding-bottom
          // becomes a window the clamped fourth line shows through. Space below comes from margin.
          sx={{
            px: 1,
            pt: 1,
            pb: 0,
            mb: 1,
            color: 'text.primary',
            // The theme's h1–h6 are Playfair (serif). The product title keeps the size and weight
            // it had before the redesign - MUI's default h6 - in the body sans.
            fontFamily: (theme) => theme.typography.body1.fontFamily,
            fontWeight: 600,
            fontSize: '1.35rem'
          }}
          variant="h4">
          {languages.bg === lang && product.name_bg}
        </Typography>

        {/* {Product.price_range} */}
        {priceRange && priceRange.length > 0 && (
          <Box sx={{ p: 1, display: 'grid', rowGap: 0.5, justifyItems: 'start' }}>
            <PriceRangeLine
              amounts={priceRange.slice(0, 2).map((price) => parseFloat(price).toFixed(2))}
              code={currency.EUR}
              amountSx={rangePriceSx}
              currencySx={variationCurrencySx}
            />
          </Box>
        )}

        {variationPrice && (
          <Box
            sx={{ p: 1, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
            <Typography component="span" sx={variationPriceSx}>
              {parseFloat(variationPrice).toFixed(2)}{' '}
              <Box component="span" sx={variationCurrencySx}>
                {currency.EUR}
              </Box>
            </Typography>
          </Box>
        )}
        <Divider />

        <Box
          sx={{
            pt: '10px',
            pb: '10px',
            pl: '10px',
            fontWeight: 600,
            color: 'text.primary'
          }}>
          {summary[0]}
        </Box>

        <List
          sx={{
            listStyleType: 'disc',
            pl: '30px',
            color: 'text.primary',
            '& li::marker': { color: 'gold.fill' }
          }}
          disablePadding>
          {summary?.map((summary, idx) => (
            <div key={idx}>
              {idx != 0 && (
                <ListItem
                  key={idx}
                  disableGutters
                  sx={{
                    display: 'list-item'
                  }}>
                  {summary}
                </ListItem>
              )}
            </div>
          ))}
        </List>
      </Grid>
    </>
  );
}
